// AlertDialog — built on top of the existing Dialog primitive.
// Provides the same API surface as shadcn's AlertDialog so pages can import
// AlertDialog/AlertDialogContent/etc. without needing @radix-ui/react-alert-dialog.
//
// IT HAS NO MOTION OF ITS OWN, ON PURPOSE. Every part below delegates to the
// Dialog primitive, so the fade-and-scale of `dialog-motion` (dialog.tsx rule 4)
// arrives here for free and cannot drift from it. A confirm that animated
// differently from a form dialog would be two dialogs, not one primitive with
// two uses.
import * as React from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

// ─── Root ─────────────────────────────────────────────────────────────────────

interface AlertDialogProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
}

function AlertDialog({ open, onOpenChange, children }: AlertDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children}
    </Dialog>
  )
}

// ─── Content ──────────────────────────────────────────────────────────────────

const AlertDialogContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <DialogContent ref={ref} className={cn('sm:max-w-[480px]', className)} {...props} />
))
AlertDialogContent.displayName = 'AlertDialogContent'

// ─── Header / Title / Description ─────────────────────────────────────────────

function AlertDialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <DialogHeader className={className} {...props} />
}

// The scrolling region, same as the dialog's. A confirm that is header + footer
// alone needs no body — and gets no divider, because the divider is the body's.
function AlertDialogBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <DialogBody className={className} {...props} />
}

function AlertDialogTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <DialogTitle className={className} {...(props as React.ComponentPropsWithoutRef<typeof DialogTitle>)} />
}

function AlertDialogDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <DialogDescription className={className} {...(props as React.ComponentPropsWithoutRef<typeof DialogDescription>)} />
}

// ─── Footer / Actions ─────────────────────────────────────────────────────────

function AlertDialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <DialogFooter className={className} {...props} />
}

interface AlertDialogActionProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

function AlertDialogAction({ className, ...props }: AlertDialogActionProps) {
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium cursor-pointer transition-colors',
        'bg-primary text-primary-foreground hover:bg-primary/90',
        'h-10 px-4 py-2',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        className
      )}
      {...props}
    />
  )
}

interface AlertDialogCancelProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

function AlertDialogCancel({ className, ...props }: AlertDialogCancelProps) {
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium cursor-pointer transition-colors',
        'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        'h-10 px-4 py-2',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        className
      )}
      {...props}
    />
  )
}

export {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
}
